import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  Carousel,
} from "react-bootstrap";

/**
 * DISCOUNT_CONTRACTOR_LandingPage_v01A_WIZARD (React + TypeScript + React-Bootstrap)
 * Version: v01A
 * Date: 2025-08-31 (America/Denver)
 *
 * Highlights vs DUI base:
 * - Wizard/Vertical toggle with step gating (no early submit).
 * - Business-focused fields for contractor risks (legal structure, ops, payroll/1099s, revenue, trades, project types, coverage interests, etc.).
 * - Blur-driven validation (green success styling), sectioned confirmation summary, modular payload.
 */

/* ============================================================
   CONFIG
============================================================ */
const VERSION = "v01A";
const SHOW_VERSION = false;
const TEST_MODE = false; // true => logs payload, skips network
const API_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const imagePath = "insurance/images/Contractor-Insurance/";

/* ============================================================
   TYPES
============================================================ */
export type LegalStructure = "" | "LLC" | "S-Corp" | "C-Corp" | "Sole Proprietorship" | "Partnership" | "Other";
export type TradeType =
  | "General Contractor" | "Remodeling" | "Carpentry" | "Electrical" | "Plumbing" | "HVAC" | "Roofing"
  | "Concrete" | "Masonry" | "Landscaping" | "Painting" | "Flooring" | "Drywall" | "Other";
export type ProjectCategory = "Residential" | "Commercial" | "Industrial" | "Public Works" | "Service/Repair" | "New Build" | "Tenant Finish";
export type CoverageInterest = "General Liability" | "Workers Comp" | "Commercial Auto" | "Tools/Equipment (Inland Marine)" | "Bonding" | "Umbrella" | "Cyber";

interface ILeadPayload_Contractor {
  // Contact
  fullName: string;
  email: string;
  phone: string; // digits only
  state: string; // mailing state
  zipCode: string;

  // Business basics
  businessName: string;
  legalStructure: LegalStructure;
  yearsInBusiness?: string; // keep string for wire
  statesOfOperation?: string[]; // optional multi-state

  // Operations / exposure
  primaryTrade: TradeType;
  otherTrade?: string;
  employeesCount: string; // numeric as string for wire
  contractorsCount: string; // 1099 count
  annualRevenue: string; // numeric as string (commas allowed prior to toWire)
  projectCategories: ProjectCategory[]; // what they do
  primaryFocus: string; // free-text short
  typicalProjectSizeMin?: string; // as string
  typicalProjectSizeMax?: string;
  workRadiusMiles?: string;
  recentClaims?: string; // yes/no + details (free text)

  // Coverage interests
  coverageDesired: CoverageInterest[];
  currentPolicies?: string; // carrier or notes

  // Misc
  notes?: string;
  utm?: { source?: string; medium?: string; campaign?: string };
  domainName: string;
  itemCategory: string; // e.g., Customer.contractor.quote
  itemClass: string; // quote.request
  itemType: string; // contractor.landingPage
  clientVersion: string;
}

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

function digitsOnly(s: string): string { return (s || "").replace(/\D+/g, ""); }
function isValidEmail(email: string): boolean { return /.+@.+\..+/.test(email); }
function isValidPhone(raw: string): boolean { return digitsOnly(raw).length === 10; }
function isValidZipCode(raw: string): boolean { return digitsOnly(raw).length === 5; }
function formatPhonePretty(raw: string): string {
  const d = digitsOnly(raw);
  if (d.length !== 10) return raw;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

const TRADE_OPTIONS: TradeType[] = [
  "General Contractor","Remodeling","Carpentry","Electrical","Plumbing","HVAC","Roofing",
  "Concrete","Masonry","Landscaping","Painting","Flooring","Drywall","Other"
];

const PROJECT_OPTIONS: ProjectCategory[] = [
  "Residential","Commercial","Industrial","Public Works","Service/Repair","New Build","Tenant Finish"
];

const COVERAGE_OPTIONS: CoverageInterest[] = [
  "General Liability","Workers Comp","Commercial Auto","Tools/Equipment (Inland Marine)","Bonding","Umbrella","Cyber"
];

/* ============================================================
   SLIDES
============================================================ */
interface Slide { src: string; alt: string; title: string; subline: string; smallCaption: string; }
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_ContractorTeam_Preview.jpg",
    alt: "Contractor crew on site with safety gear",
    title: "Discount Contractor Insurance",
    subline: "Fast quotes for GC and artisan trades — we shop multiple carriers.",
    smallCaption: "Built for busy contractors.",
  },
  {
    src: imagePath + "AdobeStock_ToolsTruck_Preview.jpg",
    alt: "Work truck with tools and ladder",
    title: "Cover Your Work & Your Crew",
    subline: "GL, Workers Comp, Auto, Tools, Umbrella, Bonding — we’ll guide you.",
    smallCaption: "Bundle and save.",
  },
  {
    src: imagePath + "AdobeStock_JobsiteRoof_Preview.jpg",
    alt: "Rooftop job with fall protection",
    title: "Built Around Your Projects",
    subline: "Residential, commercial, tenant finish, public works — we tailor coverage.",
    smallCaption: "Right fit, right price.",
  },
];

/* ============================================================
   MAIN COMPONENT
============================================================ */
export const DISCOUNT_CONTRACTOR_LandingPage_v02K_WIZARD: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Contact
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  // Business basics
  const [businessName, setBusinessName] = useState("");
  const [legalStructure, setLegalStructure] = useState<LegalStructure>("");
  const [yearsInBusiness, setYearsInBusiness] = useState("");
  const [statesOfOperation, setStatesOfOperation] = useState<string[]>([]);

  // Operations / exposure
  const [primaryTrade, setPrimaryTrade] = useState<TradeType>("General Contractor");
  const [otherTrade, setOtherTrade] = useState("");
  const [employeesCount, setEmployeesCount] = useState("");
  const [contractorsCount, setContractorsCount] = useState("");
  const [annualRevenue, setAnnualRevenue] = useState("");
  const [projectCategories, setProjectCategories] = useState<ProjectCategory[]>(["Residential"]);
  const [primaryFocus, setPrimaryFocus] = useState("");
  const [typicalMin, setTypicalMin] = useState("");
  const [typicalMax, setTypicalMax] = useState("");
  const [workRadiusMiles, setWorkRadiusMiles] = useState("");
  const [recentClaims, setRecentClaims] = useState("");

  // Coverage interests
  const [coverageDesired, setCoverageDesired] = useState<CoverageInterest[]>(["General Liability"]);
  const [currentPolicies, setCurrentPolicies] = useState("");

  // Misc
  const [notes, setNotes] = useState("");

  // Submit / server state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] = useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["Contact & Business", "Operations", "Coverages & Submit", "Confirmation"] as const;

  /* ---------- Blur-driven validity helpers ---------- */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));
  const vp = (name: string, pass: boolean) => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  /* ---------- Validators ---------- */
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),
    state: state !== "",
    zip: isValidZipCode(zipCode),

    businessName: businessName.trim().length >= 2,
    legalStructure: legalStructure !== "",

    primaryTrade: !!primaryTrade,
    employeesCount: employeesCount.trim() !== "" && /^\d+$/.test(employeesCount.trim()),
    contractorsCount: contractorsCount.trim() === "" || /^\d+$/.test(contractorsCount.trim()),
    annualRevenue: annualRevenue.trim() !== "" && /^\d{1,3}(,?\d{3})*(\.\d{1,2})?$/.test(annualRevenue.trim()) || /^\d+$/.test(annualRevenue.trim()),
    projectCategories: projectCategories.length >= 1,
    primaryFocus: primaryFocus.trim().length >= 2,

    coverageDesired: coverageDesired.length >= 1,
  };

  // Overall validity
  const valid = useMemo(() => (
    V.fullName && V.email && V.phone && V.state && V.zip &&
    V.businessName && V.legalStructure &&
    V.primaryTrade && V.employeesCount && V.annualRevenue && V.projectCategories && V.primaryFocus &&
    V.coverageDesired
  ), [V]);

  /* ---------- Wizard helpers ---------- */
  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0:
        return V.fullName && V.email && V.phone && V.state && V.zip && V.businessName && V.legalStructure;
      case 1:
        return V.primaryTrade && V.employeesCount && V.annualRevenue && V.projectCategories && V.primaryFocus;
      case 2:
        return V.coverageDesired; // final step also checks overall valid before submit
      case 3:
        return submitOk === true;
      default:
        return false;
    }
  }

  const goNext = () => {
    const touchMany = (names: string[]) => setTouched((t) => names.reduce((acc, n) => ({ ...acc, [n]: true }), t));
    if (currentStep === 0) touchMany(["fullName","email","phone","state","zip","businessName","legalStructure"]);
    if (currentStep === 1) touchMany(["primaryTrade","employeesCount","annualRevenue","projectCategories","primaryFocus"]);
    if (currentStep === 2) touchMany(["coverageDesired"]);
    if (!isStepValid(currentStep)) return;
    setCurrentStep((s) => Math.min(3, s + 1));
  };
  const goBack = () => setCurrentStep((s) => Math.max(0, s - 1));

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ---------- Selections handlers ---------- */
  function toggleFromArray<T>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  /* ---------- Submit ---------- */
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    // In wizard mode, ignore submit on steps 0–1 and just advance
    if (isWizard && currentStep < 2) {
      goNext();
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // touch all key fields
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true, state: true, zip: true,
      businessName: true, legalStructure: true,
      primaryTrade: true, employeesCount: true, annualRevenue: true, projectCategories: true, primaryFocus: true,
      coverageDesired: true,
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const params = new URLSearchParams(window.location.search);

    const payload_DATA: ILeadPayload_Contractor = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),
      state: String(state),
      zipCode: String(zipCode),

      businessName: businessName.trim(),
      legalStructure,
      yearsInBusiness: yearsInBusiness.trim() || undefined,
      statesOfOperation: statesOfOperation.length ? statesOfOperation : undefined,

      primaryTrade,
      otherTrade: primaryTrade === "Other" && otherTrade.trim() ? otherTrade.trim() : undefined,
      employeesCount: employeesCount.trim(),
      contractorsCount: contractorsCount.trim() || "0",
      annualRevenue: annualRevenue.trim(),
      projectCategories,
      primaryFocus: primaryFocus.trim(),
      typicalProjectSizeMin: typicalMin.trim() || undefined,
      typicalProjectSizeMax: typicalMax.trim() || undefined,
      workRadiusMiles: workRadiusMiles.trim() || undefined,
      recentClaims: recentClaims.trim() || undefined,

      coverageDesired,
      currentPolicies: currentPolicies.trim() || undefined,

      notes: notes.trim() || undefined,
      utm: {
        source: params.get("utm_source") || undefined,
        medium: params.get("utm_medium") || undefined,
        campaign: params.get("utm_campaign") || undefined,
      },
      domainName: String(window.location.hostname),
      itemCategory: "Customer.contractor.quote",
      itemClass: "quote.request",
      itemType: "contractor.landingPage",
      clientVersion: String(VERSION),
    };

    try {
      setSubmitting(true);
      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload:", payload_DATA);
        await new Promise((r) => setTimeout(r, 600));
        setServerStatus({ ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) });
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload_DATA),
        mode: "cors",
      });

      let resJson: any = null;
      try { resJson = await res.json(); } catch {}

      if (!res.ok) {
        const msg = (resJson && (resJson.error || resJson.message)) || `Request failed: ${res.status}`;
        setServerStatus({ ok: false, status: res.status, message: msg });
        throw new Error(msg);
      }

      setServerStatus({ ok: true, status: res.status, id: resJson?.id || resJson?.requestId });
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(msg);
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Confirmation block ---------- */
  const ConfirmationBlock: React.FC = () => (
    <fieldset className="border rounded-3 p-3 bg-body-secondary">
      <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
        <strong>Confirmation</strong>
      </legend>

      {submitOk ? (
        <>
          <Alert variant="success" className="mb-3">
            <div className="fw-semibold mb-1">Thank you, a representative will be contacting you.</div>
            <div className="small mb-0">
              {serverStatus?.ok ? (
                <>Server status: <strong>OK</strong>{serverStatus?.status ? ` • ${serverStatus.status}` : ""}{serverStatus?.id ? ` • ID: ${serverStatus.id}` : ""}</>
              ) : (
                <>Server status: Unknown</>
              )}
            </div>
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Contact</Card.Header>
                <Card.Body className="small">
                  <div><strong>Name:</strong> {fullName}</div>
                  <div><strong>Email:</strong> {email}</div>
                  <div><strong>Phone:</strong> {formatPhonePretty(phone)}</div>
                  <div><strong>State/Zip:</strong> {state} {zipCode}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Business</Card.Header>
                <Card.Body className="small">
                  <div><strong>Business Name:</strong> {businessName}</div>
                  <div><strong>Legal Structure:</strong> {legalStructure}</div>
                  <div><strong>Years in Business:</strong> {yearsInBusiness || "—"}</div>
                  <div><strong>States of Operation:</strong> {statesOfOperation.length ? statesOfOperation.join(", ") : "—"}</div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Operations</Card.Header>
                <Card.Body className="small">
                  <Row className="g-2">
                    <Col md={6}><strong>Primary Trade:</strong> {primaryTrade}{primaryTrade === "Other" && otherTrade ? ` (${otherTrade})` : ""}</Col>
                    <Col md={6}><strong>Employees:</strong> {employeesCount} &nbsp; <strong>1099s:</strong> {contractorsCount || "0"}</Col>
                    <Col md={6}><strong>Annual Revenue:</strong> {annualRevenue}</Col>
                    <Col md={6}><strong>Work Radius (mi):</strong> {workRadiusMiles || "—"}</Col>
                    <Col md={12}><strong>Project Types:</strong> {projectCategories.join(", ")}</Col>
                    <Col md={12}><strong>Primary Focus:</strong> {primaryFocus}</Col>
                    <Col md={6}><strong>Typical Size Min:</strong> {typicalMin || "—"}</Col>
                    <Col md={6}><strong>Typical Size Max:</strong> {typicalMax || "—"}</Col>
                    <Col md={12}><strong>Recent Claims:</strong> {recentClaims || "—"}</Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={12}>
              <Card>
                <Card.Header className="fw-semibold">Coverage Interests</Card.Header>
                <Card.Body className="small">
                  <Row>
                    <Col md={6}><strong>Desired:</strong> {coverageDesired.join(", ")}</Col>
                    <Col md={6}><strong>Current Policies:</strong> {currentPolicies || "—"}</Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {notes.trim() && (
              <Col md={12}>
                <Card>
                  <Card.Header className="fw-semibold">Notes</Card.Header>
                  <Card.Body className="small">{notes}</Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                // reset for new quote
                setFullName(""); setEmail(""); setPhone(""); setState(""); setZipCode("");
                setBusinessName(""); setLegalStructure(""); setYearsInBusiness(""); setStatesOfOperation([]);
                setPrimaryTrade("General Contractor"); setOtherTrade("");
                setEmployeesCount(""); setContractorsCount(""); setAnnualRevenue("");
                setProjectCategories(["Residential"]); setPrimaryFocus("");
                setTypicalMin(""); setTypicalMax(""); setWorkRadiusMiles(""); setRecentClaims("");
                setCoverageDesired(["General Liability"]); setCurrentPolicies("");
                setNotes(""); setTouched({}); setSubmitOk(false); setServerStatus(null); setCurrentStep(0);
                scrollToForm();
              }}
            >
              Start New Quote
            </Button>
          </div>
        </>
      ) : (
        <Alert variant="warning">We couldn’t confirm the server response. Please go back and try submitting again.</Alert>
      )}
    </fieldset>
  );

  const active = slides[activeSlide];

  /* ------------------------------------------------------------
     UI
  ------------------------------------------------------------ */
  return (
    <div className="bg-body">
      {/* Sticky Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            Discount <span className="text-primary">Contractor</span> Insurance
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              <div className="d-none d-md-block text-body-secondary me-2">Speak with an agent:</div>
              <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO with background Carousel */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "760px" }}>
        <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(i)}
            controls
            indicators
            fade
            interval={3500}
            className="w-100 h-100"
          >
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100">
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-4 p-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "400px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Multiple Carriers</Badge>
                  <Badge bg="light" text="dark">Licensed Advisors</Badge>
                  <Badge bg="light" text="dark">Fast Certificates</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" href="#quote">Start My Quote</Button>
                </div>
              </div>
            </Col>

            <Col lg={6} id="quote">
              <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                <Card.Header className="bg-white">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">Fast Contractor Insurance Quote</div>
                      <small className="text-body-secondary">Tell us about your business and projects.</small>
                    </div>
                    <Badge bg="success">Secure</Badge>
                  </div>
                </Card.Header>
                <Card.Body ref={formRef}>
                  {submitError && (
                    <Alert variant="danger" onClose={() => setSubmitError(null)} dismissible>{submitError}</Alert>
                  )}

                  {/* MODE TOGGLE + (Wizard) Stepper */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="switch"
                        id="modeSwitch"
                        label={isWizard ? "Wizard mode" : "Vertical mode"}
                        checked={isWizard}
                        onChange={(e) => {
                          setMode(e.currentTarget.checked ? "wizard" : "vertical");
                          setTouched({});
                          if (submitOk && e.currentTarget.checked) setCurrentStep(3); else setCurrentStep(0);
                        }}
                      />
                      <small className="text-muted">Switch to see all fields in one view.</small>
                    </div>

                    <div style={{ minWidth: 280 }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                        <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                      </div>
                      <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                    </div>
                  </div>

                  <Form
                    onSubmit={handleSubmit}
                    noValidate
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && isWizard) {
                        if (currentStep < 2) {
                          e.preventDefault(); e.stopPropagation();
                          if (isStepValid(currentStep)) goNext();
                        }
                      }
                    }}
                  >
                    {isWizard ? (
                      <>
                        <Row className="g-3">
                          {/* STEP 1: CONTACT & BUSINESS */}
                          {currentStep === 0 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Contact &amp; Business</strong>
                                </legend>
                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="fullName">Full Name</Form.Label>
                                    <Form.Control id="fullName" value={fullName} onChange={(e)=>setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
                                    <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                  </Col>
                                  <Col md={6}>
                                    <Form.Label htmlFor="email">Email</Form.Label>
                                    <Form.Control id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
                                    <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="phone">Phone</Form.Label>
                                    <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
                                    <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="state">Mailing State</Form.Label>
                                    <Form.Select id="state" value={state} onChange={(e)=>setState(e.target.value)} required {...vp("state", V.state)}>
                                      <option value="">Select state</option>
                                      {US_STATES.map((s)=> <option key={s.code} value={s.code}>{s.name}</option>)}
                                    </Form.Select>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="zip">Zip Code</Form.Label>
                                    <Form.Control id="zip" inputMode="numeric" value={zipCode} onChange={(e)=>setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
                                    <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={8}>
                                    <Form.Label htmlFor="bizName">Business Name</Form.Label>
                                    <Form.Control id="bizName" value={businessName} onChange={(e)=>setBusinessName(e.target.value)} placeholder="ABC Construction LLC" required {...vp("businessName", V.businessName)} />
                                    <Form.Control.Feedback type="invalid">Enter your business name.</Form.Control.Feedback>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="structure">Legal Structure</Form.Label>
                                    <Form.Select id="structure" value={legalStructure} onChange={(e)=>setLegalStructure(e.target.value as LegalStructure)} required {...vp("legalStructure", V.legalStructure)}>
                                      <option value="">Select</option>
                                      <option>LLC</option>
                                      <option>S-Corp</option>
                                      <option>C-Corp</option>
                                      <option>Sole Proprietorship</option>
                                      <option>Partnership</option>
                                      <option>Other</option>
                                    </Form.Select>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="years">Years in Business (optional)</Form.Label>
                                    <Form.Control id="years" value={yearsInBusiness} onChange={(e)=>setYearsInBusiness(e.target.value)} placeholder="e.g., 5" />
                                  </Col>
                                  <Col md={8}>
                                    <Form.Label>States of Operation (optional)</Form.Label>
                                    <InputGroup>
                                      <Form.Select multiple value={statesOfOperation} onChange={(e)=>{
                                        const opts = Array.from(e.currentTarget.selectedOptions).map(o=>o.value);
                                        setStatesOfOperation(opts);
                                      }}>
                                        {US_STATES.map((s)=> <option key={s.code} value={s.code}>{s.code}</option>)}
                                      </Form.Select>
                                    </InputGroup>
                                    <div className="form-text">Hold Ctrl/Cmd to select multiple.</div>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 2: OPERATIONS */}
                          {currentStep === 1 && (
                            <Col xs={12}>
                              <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                  <strong>Operations</strong>
                                </legend>

                                <Row className="g-3 mt-1">
                                  <Col md={6}>
                                    <Form.Label htmlFor="trade">Primary Trade</Form.Label>
                                    <Form.Select id="trade" value={primaryTrade} onChange={(e)=>setPrimaryTrade(e.target.value as TradeType)} {...vp("primaryTrade", V.primaryTrade)}>
                                      {TRADE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </Form.Select>
                                  </Col>
                                  {primaryTrade === "Other" && (
                                    <Col md={6}>
                                      <Form.Label htmlFor="otherTrade">Describe Trade</Form.Label>
                                      <Form.Control id="otherTrade" value={otherTrade} onChange={(e)=>setOtherTrade(e.target.value)} placeholder="e.g., Solar Install, Low-Voltage, Fencing" />
                                    </Col>
                                  )}

                                  <Col md={4}>
                                    <Form.Label htmlFor="revenue">Annual Sales Revenue</Form.Label>
                                    <Form.Control id="revenue" inputMode="text" value={annualRevenue} onChange={(e)=>setAnnualRevenue(e.target.value)} placeholder="e.g., 750,000" required {...vp("annualRevenue", V.annualRevenue)} />
                                    <Form.Control.Feedback type="invalid">Enter a number (commas OK).</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="emps"># W-2 Employees</Form.Label>
                                    <Form.Control id="emps" inputMode="numeric" value={employeesCount} onChange={(e)=>setEmployeesCount(e.target.value)} placeholder="e.g., 3" required {...vp("employeesCount", V.employeesCount)} />
                                    <Form.Control.Feedback type="invalid">Enter a whole number.</Form.Control.Feedback>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="subs"># 1099 Contractors </Form.Label>
                                    <Form.Control id="subs" inputMode="numeric" value={contractorsCount} onChange={(e)=>setContractorsCount(e.target.value)} placeholder="e.g., 2" {...vp("contractorsCount", V.contractorsCount || contractorsCount.trim()==="") } />
                                    <p>(optional)</p>
                                  
                                  </Col>
                                  

                                  <Col md={12}>
                                    <Form.Label>Project Types (choose all that apply)</Form.Label>
                                    <div className="d-flex flex-wrap gap-2">
                                      {PROJECT_OPTIONS.map(p => (
                                        <Form.Check
                                          key={p}
                                          type="switch"
                                          id={`proj-${p}`}
                                          label={p}
                                          checked={projectCategories.includes(p)}
                                          onChange={() => setProjectCategories(prev => toggleFromArray(prev, p))}
                                        />
                                      ))}
                                    </div>
                                    <Form.Text className={projectCategories.length ? "text-success" : "text-danger"}>
                                      {projectCategories.length ? `${projectCategories.length} selected` : "Select at least one."}
                                    </Form.Text>
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="focus">Primary Focus of the business</Form.Label>
                                    <Form.Control id="focus" as="textarea" value={primaryFocus} onChange={(e)=>setPrimaryFocus(e.target.value)} placeholder="e.g., Residential kitchen & bath remodels; some tenant finish." required {...vp("primaryFocus", V.primaryFocus)} />
                                    <Form.Control.Feedback type="invalid">Please add a short description.</Form.Control.Feedback>
                                  </Col>

                                  <Col md={4}>
                                    <Form.Label htmlFor="min">Typical Project - Size Min</Form.Label>
                                    <Form.Control id="min" value={typicalMin} onChange={(e)=>setTypicalMin(e.target.value)} placeholder="$5,000" />
                                        <p>(optional)</p>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="max">Typical Project - Size Max</Form.Label>
                                    <Form.Control id="max" value={typicalMax} onChange={(e)=>setTypicalMax(e.target.value)} placeholder="$500,000" />
                                        <p>(optional)</p>
                                  </Col>
                                  <Col md={4}>
                                    <Form.Label htmlFor="radius">Work Radius</Form.Label>
                                    <Form.Control id="radius" value={workRadiusMiles} onChange={(e)=>setWorkRadiusMiles(e.target.value)} placeholder="e.g., 50" />
                                    <p>(miles, optional)</p>
                                  </Col>

                                  <Col md={12}>
                                    <Form.Label htmlFor="claims">Recent Claims or Losses? (optional)</Form.Label>
                                    <Form.Control id="claims" as="textarea" rows={2} value={recentClaims} onChange={(e)=>setRecentClaims(e.target.value)} placeholder="e.g., GL claim 2023 - $4,200 - settled; WC claim 2022 back strain; none if not applicable." />
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          )}

                          {/* STEP 3: COVERAGES & SUBMIT */}
                          {currentStep === 2 && (
                            <>
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Coverages</strong>
                                  </legend>

                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label>What coverages would you like quotes for?</Form.Label>
                                      <div className="d-flex flex-wrap gap-2">
                                        {COVERAGE_OPTIONS.map(c => (
                                          <Form.Check
                                            key={c}
                                            type="switch"
                                            id={`cov-${c}`}
                                            label={c}
                                            checked={coverageDesired.includes(c)}
                                            onChange={() => setCoverageDesired(prev => toggleFromArray(prev, c))}
                                          />
                                        ))}
                                      </div>
                                      <Form.Text className={coverageDesired.length ? "text-success" : "text-danger"}>
                                        {coverageDesired.length ? `${coverageDesired.length} selected` : "Select at least one."}
                                      </Form.Text>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="currentPolicies">Current Policies / Carrier (optional)</Form.Label>
                                      <Form.Control id="currentPolicies" value={currentPolicies} onChange={(e)=>setCurrentPolicies(e.target.value)} placeholder="e.g., GL with ABC Insurance; Auto with XYZ; renewal in November." />
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="notes">Notes (optional)</Form.Label>
                                      <Form.Control id="notes" as="textarea" rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Permits, bonds, special requirements, cert holder names, etc." />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </>
                          )}

                          {/* STEP 4: CONFIRMATION */}
                          {currentStep === 3 && (
                            <Col xs={12}>
                              <ConfirmationBlock />
                            </Col>
                          )}
                        </Row>

                        {/* Wizard controls */}
                        {currentStep < 3 && (
                          <div className="d-flex justify-content-between mt-3">
                            <Button type="button" variant="outline-secondary" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); goBack(); }} disabled={currentStep === 0}>Previous</Button>
                            {currentStep < 2 ? (
                              <Button type="button" onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); if (isStepValid(currentStep)) goNext(); }} disabled={!isStepValid(currentStep)}>Next</Button>
                            ) : (
                              <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2"/> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      // ---------- VERTICAL (all-in-one) ----------
                      <>
                        {!submitOk ? (
                          <>
                            {/* Contact & Business */}
                            <Row className="g-3">
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Contact &amp; Business</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="fullNameV">Full Name</Form.Label>
                                      <Form.Control id="fullNameV" value={fullName} onChange={(e)=>setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
                                      <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Label htmlFor="emailV">Email</Form.Label>
                                      <Form.Control id="emailV" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
                                      <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="phoneV">Phone</Form.Label>
                                      <Form.Control id="phoneV" inputMode="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
                                      <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="stateV">Mailing State</Form.Label>
                                      <Form.Select id="stateV" value={state} onChange={(e)=>setState(e.target.value)} required {...vp("state", V.state)}>
                                        <option value="">Select state</option>
                                        {US_STATES.map((s)=> <option key={s.code} value={s.code}>{s.name}</option>)}
                                      </Form.Select>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="zipV">Zip Code</Form.Label>
                                      <Form.Control id="zipV" inputMode="numeric" value={zipCode} onChange={(e)=>setZipCode(e.target.value)} placeholder="#####" required {...vp("zip", V.zip)} />
                                      <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={8}>
                                      <Form.Label htmlFor="bizNameV">Business Name</Form.Label>
                                      <Form.Control id="bizNameV" value={businessName} onChange={(e)=>setBusinessName(e.target.value)} placeholder="ABC Construction LLC" required {...vp("businessName", V.businessName)} />
                                      <Form.Control.Feedback type="invalid">Enter your business name.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="structureV">Legal Structure</Form.Label>
                                      <Form.Select id="structureV" value={legalStructure} onChange={(e)=>setLegalStructure(e.target.value as LegalStructure)} required {...vp("legalStructure", V.legalStructure)}>
                                        <option value="">Select</option>
                                        <option>LLC</option>
                                        <option>S-Corp</option>
                                        <option>C-Corp</option>
                                        <option>Sole Proprietorship</option>
                                        <option>Partnership</option>
                                        <option>Other</option>
                                      </Form.Select>
                                    </Col>

                                    <Col md={4}>
                                      <Form.Label htmlFor="yearsV">Years in Business (optional)</Form.Label>
                                      <Form.Control id="yearsV" value={yearsInBusiness} onChange={(e)=>setYearsInBusiness(e.target.value)} placeholder="e.g., 5" />
                                    </Col>
                                    <Col md={8}>
                                      <Form.Label>States of Operation (optional)</Form.Label>
                                      <InputGroup>
                                        <Form.Select multiple value={statesOfOperation} onChange={(e)=>{
                                          const opts = Array.from(e.currentTarget.selectedOptions).map(o=>o.value);
                                          setStatesOfOperation(opts);
                                        }}>
                                          {US_STATES.map((s)=> <option key={s.code} value={s.code}>{s.code}</option>)}
                                        </Form.Select>
                                      </InputGroup>
                                      <div className="form-text">Hold Ctrl/Cmd to select multiple.</div>
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Operations */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Operations</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={6}>
                                      <Form.Label htmlFor="tradeV">Primary Trade</Form.Label>
                                      <Form.Select id="tradeV" value={primaryTrade} onChange={(e)=>setPrimaryTrade(e.target.value as TradeType)} {...vp("primaryTrade", V.primaryTrade)}>
                                        {TRADE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                                      </Form.Select>
                                    </Col>
                                    {primaryTrade === "Other" && (
                                      <Col md={6}>
                                        <Form.Label htmlFor="otherTradeV">Describe Trade</Form.Label>
                                        <Form.Control id="otherTradeV" value={otherTrade} onChange={(e)=>setOtherTrade(e.target.value)} placeholder="e.g., Solar Install, Low-Voltage, Fencing" />
                                      </Col>
                                    )}

                                    <Col md={4}>
                                      <Form.Label htmlFor="empsV"># W-2 Employees</Form.Label>
                                      <Form.Control id="empsV" inputMode="numeric" value={employeesCount} onChange={(e)=>setEmployeesCount(e.target.value)} placeholder="e.g., 3" required {...vp("employeesCount", V.employeesCount)} />
                                      <Form.Control.Feedback type="invalid">Enter a whole number.</Form.Control.Feedback>
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="subsV"># 1099 Contractors (optional)</Form.Label>
                                      <Form.Control id="subsV" inputMode="numeric" value={contractorsCount} onChange={(e)=>setContractorsCount(e.target.value)} placeholder="e.g., 2" {...vp("contractorsCount", V.contractorsCount || contractorsCount.trim()==="") } />
                                    </Col>
                                    <Col md={4}>
                                      <Form.Label htmlFor="revenueV">Annual Sales Revenue</Form.Label>
                                      <Form.Control id="revenueV" inputMode="text" value={annualRevenue} onChange={(e)=>setAnnualRevenue(e.target.value)} placeholder="e.g., 750,000" required {...vp("annualRevenue", V.annualRevenue)} />
                                      <Form.Control.Feedback type="invalid">Enter a number (commas OK).</Form.Control.Feedback>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label>Project Types (choose all that apply)</Form.Label>
                                      <div className="d-flex flex-wrap gap-2">
                                        {PROJECT_OPTIONS.map(p => (
                                          <Form.Check key={p} type="switch" id={`projV-${p}`} label={p} checked={projectCategories.includes(p)} onChange={() => setProjectCategories(prev => toggleFromArray(prev, p))} />
                                        ))}
                                      </div>
                                      <Form.Text className={projectCategories.length ? "text-success" : "text-danger"}>
                                        {projectCategories.length ? `${projectCategories.length} selected` : "Select at least one."}
                                      </Form.Text>
                                    </Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="focusV">Primary Focus (brief)</Form.Label>
                                      <Form.Control id="focusV" value={primaryFocus} onChange={(e)=>setPrimaryFocus(e.target.value)} placeholder="e.g., Residential kitchen & bath remodels; some tenant finish." required {...vp("primaryFocus", V.primaryFocus)} />
                                      <Form.Control.Feedback type="invalid">Please add a short description.</Form.Control.Feedback>
                                    </Col>

                                    <Col md={4}><Form.Label htmlFor="minV">Typical Project Size Min (optional)</Form.Label><Form.Control id="minV" value={typicalMin} onChange={(e)=>setTypicalMin(e.target.value)} placeholder="$5,000" /></Col>
                                    <Col md={4}><Form.Label htmlFor="maxV">Typical Project Size Max (optional)</Form.Label><Form.Control id="maxV" value={typicalMax} onChange={(e)=>setTypicalMax(e.target.value)} placeholder="$500,000" /></Col>
                                    <Col md={4}><Form.Label htmlFor="radiusV">Work Radius (miles, optional)</Form.Label><Form.Control id="radiusV" value={workRadiusMiles} onChange={(e)=>setWorkRadiusMiles(e.target.value)} placeholder="e.g., 50" /></Col>

                                    <Col md={12}>
                                      <Form.Label htmlFor="claimsV">Recent Claims or Losses? (optional)</Form.Label>
                                      <Form.Control id="claimsV" as="textarea" rows={2} value={recentClaims} onChange={(e)=>setRecentClaims(e.target.value)} placeholder="e.g., GL claim 2023 - $4,200 - settled; none if N/A." />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>

                              {/* Coverages */}
                              <Col xs={12}>
                                <fieldset className="border rounded-3 p-3 bg-body-secondary">
                                  <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
                                    <strong>Coverages</strong>
                                  </legend>
                                  <Row className="g-3 mt-1">
                                    <Col md={12}>
                                      <Form.Label>What coverages would you like quotes for?</Form.Label>
                                      <div className="d-flex flex-wrap gap-2">
                                        {COVERAGE_OPTIONS.map(c => (
                                          <Form.Check key={c} type="switch" id={`covV-${c}`} label={c} checked={coverageDesired.includes(c)} onChange={() => setCoverageDesired(prev => toggleFromArray(prev, c))} />
                                        ))}
                                      </div>
                                      <Form.Text className={coverageDesired.length ? "text-success" : "text-danger"}>
                                        {coverageDesired.length ? `${coverageDesired.length} selected` : "Select at least one."}
                                      </Form.Text>
                                    </Col>
                                    <Col md={12}>
                                      <Form.Label htmlFor="currentPoliciesV">Current Policies / Carrier (optional)</Form.Label>
                                      <Form.Control id="currentPoliciesV" value={currentPolicies} onChange={(e)=>setCurrentPolicies(e.target.value)} placeholder="e.g., GL with ABC Insurance; Auto with XYZ; renewal in November." />
                                    </Col>
                                    <Col md={12}>
                                      <Form.Label htmlFor="notesV">Notes (optional)</Form.Label>
                                      <Form.Control id="notesV" as="textarea" rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} placeholder="Permits, bonds, special requirements, cert holder names, etc." />
                                    </Col>
                                  </Row>
                                </fieldset>
                              </Col>
                            </Row>

                            {/* Submit */}
                            <div className="d-grid mt-3">
                              <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                {submitting ? (<><Spinner animation="border" size="sm" className="me-2"/> Submitting...</>) : (<>Get My Quote</>)}
                              </Button>
                              <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                By clicking, you agree we may contact you about insurance options. No spam. You can opt out anytime.
                              </div>
                            </div>
                          </>
                        ) : (
                          <ConfirmationBlock />
                        )}
                      </>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light border-top border-bottom">
        <Container>
          <Row className="mb-4">
            <Col lg={8}>
              <h2 className="fw-bold mb-1">How It Works</h2>
              <p className="text-body-secondary mb-0">Three simple steps to get covered.</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">1</div>
                <div>
                  <h6 className="mb-1">Tell us about your business</h6>
                  <p className="mb-0 text-body-secondary">Share your trade, revenues, and project types. Takes minutes.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">2</div>
                <div>
                  <h6 className="mb-1">We compare options</h6>
                  <p className="mb-0 text-body-secondary">We shop multiple carriers for coverage that matches your operations.</p>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="d-flex align-items-start gap-3">
                <div className="badge rounded-pill text-bg-primary fs-6 mt-1">3</div>
                <div>
                  <h6 className="mb-1">You choose &amp; bind</h6>
                  <p className="mb-0 text-body-secondary">Pick the policy and get certificates fast for jobs and subs.</p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} Discount Contractor Insurance. All rights reserved.
              <div className="mt-2">Insurance services subject to licensing and state availability. This page is for general information and not legal advice.</div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              <Button variant="secondary" size="sm" href="tel:3033375434">Call {AGENT_PHONE_NUMBER}</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default DISCOUNT_CONTRACTOR_LandingPage_v02K_WIZARD;
